// Lightweight permission helpers driven by the role we cache in localStorage
// at login (and refresh on /api/auth/validate_token).
//
// Roles (per organization the user is currently viewing):
//   - 'owner' : the user owns this organization; full access including
//               billing and member management.
//   - 'admin' : can manage members and edit everything in this org,
//               but cannot delete the org or transfer ownership and
//               cannot change billing.
//   - 'write' : a member that can view AND modify bots/content but
//               cannot manage members or billing.
//   - 'read'  : a member that can only view things; cannot save edits.
//
// If nothing is set yet (e.g. existing users from before this feature
// shipped, or the role hasn't been refreshed yet) we default to 'owner'
// so the UI stays unchanged for them. The backend remains the source of
// truth — these helpers only drive UI gating; the API will still 403.

const ROLE_KEY = "role";
const OWNER_KEY = "owner_user_id";
const ORG_KEY = "current_org_id";

export const setRole = (role, ownerUserId) => {
  if (role) localStorage.setItem(ROLE_KEY, role);
  if (ownerUserId !== undefined && ownerUserId !== null) {
    localStorage.setItem(OWNER_KEY, String(ownerUserId));
  }
};

export const clearRole = () => {
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(OWNER_KEY);
  localStorage.removeItem(ORG_KEY);
};

export const getRole = () => localStorage.getItem(ROLE_KEY) || "owner";

export const getOwnerUserId = () => {
  const v = localStorage.getItem(OWNER_KEY);
  return v ? Number(v) : null;
};

export const setCurrentOrgId = (orgId) => {
  if (orgId !== undefined && orgId !== null) {
    localStorage.setItem(ORG_KEY, String(orgId));
  }
};

export const getCurrentOrgId = () => {
  const v = localStorage.getItem(ORG_KEY);
  return v ? Number(v) : null;
};

export const clearCurrentOrgId = () => localStorage.removeItem(ORG_KEY);

// Bot selections are scoped to a single workspace. When the active
// workspace changes (org switch, leave-workspace, invite-accept) the
// stored selections from the previous workspace become invalid: they
// point to bot ids that belong to a different organization, which the
// backend correctly rejects with 403 in `verify_bot_ownership`. Each
// org-switch site must call this before reloading so the next page
// boot starts from a clean slate and re-seeds with a bot from the new
// workspace.
export const clearWorkspaceScopedSelections = () => {
  try {
    localStorage.removeItem("selectedBotId");
    localStorage.removeItem("selectedBotIds");
    // Bot-settings deep-link target, also pinned to a specific bot.
    localStorage.removeItem("settingsTab");
  } catch (_) {}
};

// ---------- Bot-selection reconciliation -----------------------------
//
// Several pages (Live, Settings, Activity, Integration) render a "Select
// bot" dropdown whose chosen value is persisted in localStorage so it
// survives navigation. The stored value can become stale in several
// ways:
//   1. The user switched workspaces (different set of bots).
//   2. A bot was deleted in another page/tab while this one was closed.
//   3. The user manually tampered with localStorage.
//   4. A deep-link URL (`?bot_id=…`) refers to a bot in another workspace.
//
// Sending a stale id to the backend correctly 403s in
// verify_bot_ownership and the page falls apart. These helpers centralise
// the "if stored id isn't in the current bot list, fall back to the
// first one" logic and the persistence side-effects so every selector
// behaves the same.

const SELECTED_BOT_ID_KEY = "selectedBotId";
const SELECTED_BOT_IDS_KEY = "selectedBotIds";

// Single-select reconciliation. Returns the id that should be selected
// after considering the supplied bot list. Side-effect: persists the
// resolved value to localStorage (or removes it when the workspace has
// no bots at all).
export const reconcileSelectedBotId = (bots) => {
  const list = Array.isArray(bots) ? bots : [];
  let stored = "";
  try {
    stored = localStorage.getItem(SELECTED_BOT_ID_KEY) || "";
  } catch (_) {}
  const valid =
    stored && list.some((b) => String(b.id) === String(stored));
  if (valid) return stored;
  if (list.length === 0) {
    try {
      localStorage.removeItem(SELECTED_BOT_ID_KEY);
    } catch (_) {}
    return "";
  }
  const fallback = list[0].id;
  try {
    localStorage.setItem(SELECTED_BOT_ID_KEY, fallback);
  } catch (_) {}
  return fallback;
};

// Multi-select reconciliation (used by Activity). Drops ids that aren't
// in the current bot list; if nothing valid remains, seeds with the
// first available bot. Returns the resolved array and persists both
// `selectedBotIds` (the full array) and `selectedBotId` (the primary,
// kept in sync because other pages read it).
export const reconcileSelectedBotIds = (bots) => {
  const list = Array.isArray(bots) ? bots : [];
  let stored = [];
  try {
    const raw = localStorage.getItem(SELECTED_BOT_IDS_KEY);
    stored = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(stored)) stored = [];
  } catch (_) {
    stored = [];
  }
  const validIds = stored.filter((id) =>
    list.some((b) => String(b.id) === String(id))
  );
  let resolved = validIds;
  if (validIds.length === 0 && list.length > 0) {
    resolved = [list[0].id];
  }
  try {
    if (resolved.length === 0) {
      localStorage.removeItem(SELECTED_BOT_IDS_KEY);
      localStorage.removeItem(SELECTED_BOT_ID_KEY);
    } else {
      localStorage.setItem(SELECTED_BOT_IDS_KEY, JSON.stringify(resolved));
      localStorage.setItem(SELECTED_BOT_ID_KEY, resolved[0]);
    }
  } catch (_) {}
  return resolved;
};

export const isOwner = () => getRole() === "owner";

// Anyone with admin or owner privileges in the active org.
export const isAdminOrOwner = () => {
  const r = getRole();
  return r === "owner" || r === "admin";
};

// Read members can view but not modify. Owner / admin / write can modify.
export const canWrite = () => {
  const r = getRole();
  return r === "owner" || r === "admin" || r === "write";
};

// Owner + admin can manage members. Billing stays owner-only.
export const canManageTeam = () => isAdminOrOwner();
export const canManageBilling = () => isOwner();

// Client-side write guard for "Save changes" buttons.
//
// If the current user has read-only access we:
//   1. Surface the unified permission-denied modal (same one the global
//      fetch interceptor would show on a 403), and
//   2. Optionally revert any in-flight edits via the caller-supplied
//      `onDeny` callback (typically the page's `handleDiscardChanges`).
//      This avoids the annoying "save fails → user clicks away → has to
//      manually discard" flow.
//
// Returns `true` if the action is allowed, `false` if it was blocked.
export const guardWrite = (onDeny) => {
  if (canWrite()) return true;
  try {
    if (typeof onDeny === "function") onDeny();
  } catch (_) {}
  try {
    window.dispatchEvent(
      new CustomEvent("ub:permission_denied", { detail: { message: "" } })
    );
  } catch (_) {}
  return false;
};

